import React from 'react';
import { PaperClipOutlined } from '@ant-design/icons';
import Markdown from '@/components/ui/Markdown';
import {
    getBlockerDescriptionMarkdown,
    getBlockerDisplayId,
    getBlockerAttachmentCount,
    formatPriorityLabel,
} from '@hooks/projects/projectHelpers';
import {
    CardDescriptionInner,
    TaskCardSurface,
    TaskCardTopMetaRow,
    TaskCardTaskIdLabel,
    TaskCardPriorityPill,
    TaskCardPriorityPillDot,
    TaskCardKanbanStatusPill,
    TaskCardKanbanStatusDot,
    TaskCardHeadingTitle,
    TaskCardRule,
    TaskCardDescScroll,
    TaskCardAttachmentsPill,
} from '@/pages/inside/tenant/style';

const COL = { ACTIVE: 'todo', RESOLVED: 'completed' };

/**
 * Kanban card for a project blocker (Active / Resolved columns). Drag affordance is on {@link BlockerDragWrap}.
 */
export function BlockerCard({ blocker, column, onSelect }) {
    const isResolved = column === 'resolved';
    const kanbanColumn = isResolved ? COL.RESOLVED : COL.ACTIVE;
    const descriptionHtml = getBlockerDescriptionMarkdown(blocker);
    const hasDescription = descriptionHtml.length > 0;
    const priority = blocker?.priority || 'low';
    const priorityLabel = formatPriorityLabel(priority) || 'Low';
    const attachmentCount = getBlockerAttachmentCount(blocker);
    const showAttachments = attachmentCount > 0;

    return (
        <TaskCardSurface
            $kanbanColumn={kanbanColumn}
            $kanbanGrab={false}
            onClick={onSelect ? () => onSelect(blocker) : undefined}
            style={onSelect ? { cursor: 'pointer' } : { cursor: 'default' }}
        >
            <TaskCardTopMetaRow>
                <TaskCardTaskIdLabel $kanbanColumn={kanbanColumn}>{getBlockerDisplayId(blocker)}</TaskCardTaskIdLabel>
                {isResolved ? (
                    <TaskCardKanbanStatusPill>
                        <TaskCardKanbanStatusDot aria-hidden />
                        Resolved
                    </TaskCardKanbanStatusPill>
                ) : (
                    <TaskCardPriorityPill $priority={priority}>
                        <TaskCardPriorityPillDot $priority={priority} aria-hidden />
                        {priorityLabel}
                    </TaskCardPriorityPill>
                )}
            </TaskCardTopMetaRow>

            <TaskCardHeadingTitle $lineClamp1 $kanbanColumn={kanbanColumn}>
                {blocker?.title ?? blocker?.name ?? '—'}
            </TaskCardHeadingTitle>

            {hasDescription && (
                <>
                    <TaskCardRule />
                    <TaskCardDescScroll $kanbanColumn={kanbanColumn} $previewLines={3}>
                        <Markdown source={descriptionHtml} component={CardDescriptionInner} />
                    </TaskCardDescScroll>
                </>
            )}

            {showAttachments && (
                <>
                    {hasDescription && <TaskCardRule $kanbanColumn={kanbanColumn} />}
                    <TaskCardAttachmentsPill $kanbanColumn={kanbanColumn}>
                        <PaperClipOutlined style={{ fontSize: 13 }} aria-hidden />
                        Attachments
                        {attachmentCount > 1 ? ` (${attachmentCount})` : ''}
                    </TaskCardAttachmentsPill>
                </>
            )}
        </TaskCardSurface>
    );
}
